/* eslint-disable no-nested-ternary */
/* eslint-disable no-extra-parens */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import JsonQuery from 'json-query';
import parse from 'parse-json';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
    getThemeBasedColorPalette,
} from '../utils';

import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import { mergeOptions } from '../GrowthChart/mergeOptions';
import { sampleData } from './sampleData';
import PeriodChartStyles from './PeriodChart.styles';

interface highChartOptions {
    path?: string;
    value?: string;
}

export interface ZoneOption {
    year: string;
    color: string;
}

export interface textLabel {
    text: string;
    align: string;
    x: string;
    y: string;
    style: string;
}

interface plotBands {
    from: string;
    to: string;
    color: string;
}

interface plotLines {
    value: string;
    color: string;
    label: textLabel;
    width: number;
    dashStyle: string;
    zIndex: number;
}

interface toolTipOptions {
    label: string;
    mappingkey: string;
    format: string;
    className: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    dataKey: string;
    useSampleData: boolean;
    chartTitles: Headings[];
    options: highChartOptions[];
    functionToTransform: string;
    transformData?: boolean;
    paletteId?: string;
    typographyClass?: string;
    isPeriodType: boolean;
    functionToSelectPeriod?: string;
    plotLinesBuffer?: plotLines[];
    plotBandsBuffer?: plotBands[];
    plotLinesAccelerator?: plotLines[];
    plotBandsAccelerator?: plotBands[];
    toolTipBufferOptions?: toolTipOptions[];
    toolTipAcceleratorOptions?: toolTipOptions[];
    acceleratedZoneText?: string;
    min?: string;
    max?: string;
}

const PeriodChart = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType, colorPalettes } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        dataKey,
        useSampleData,
        chartTitles,
        options,
        functionToTransform,
        transformData,
        paletteId,
        isPeriodType,
        functionToSelectPeriod,
        toolTipBufferOptions,
        toolTipAcceleratorOptions,
        min,
        max,
        ...rest
    } = widgetProperties || {};
    const { data } = props;
    // const theme = data?.themeSelected;

    let showBuffer = true;

    const i18n = commonHooks.useLocalization();

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToSelectPeriod}`)();
        const periodName = isPeriodType && transformFunction(data);

        if (periodName && periodName !== 'buffer') {
            showBuffer = false;
        }
    } catch (e) {
        console.log(e);
    }

    const { value: periodsChartData }: any = JsonQuery(`${dataKey}`, {
        data: useSampleData ? sampleData : data,
    });
    const performanceData = parse(JSON.stringify(periodsChartData || []));

    let transformedData = {
        data,
        performanceData,
        options,
        min,
        max,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedProps =
            transformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                ...transformedData,
                JsonQuery,
            });

        if (transformedProps) {
            transformedData = { ...transformedData, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    let plotLinesOptions: any = [];
    let plotBandOptions: any = [];

    // map plotLinesBuffer and plotBandsBuffer
    const plotLineType = showBuffer ? rest.plotLinesBuffer : rest.plotLinesAccelerator;
    const plotBandType = showBuffer ? rest.plotBandsBuffer : rest.plotBandsAccelerator;
    plotLinesOptions = plotLineType?.map((line) => {
        return {
            zIndex: line.zIndex,
            color: line.color,
            value: Number(JsonQuery(line.value, { data: data })?.value || Number(line.value) || 0),
            width: line.width,
            label: {
                text: i18n.format(line.label.text || '', data),
                align: line.label.align,
                x: Number(line.label.x) || 0,
                y: Number(line.label.y) || 0,
                // convert style property as a string
                style: line.label.style ? JSON.parse(line.label.style) : '',
            },
            dashStyle: line.dashStyle,
        };
    });

    plotBandOptions = plotBandType?.map((band) => {
        return {
            // use jsonQuery to get the value, convert to number
            from: Number(JsonQuery(band.from, { data: data })?.value || 0),
            to: Number(JsonQuery(band.to, { data: data })?.value || 0),
            color: band.color,
        };
    });

    try {
        // eslint-disable-next-line no-new-func
        const transformedProps =
            transformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                ...transformedData,
                JsonQuery,
            });

        if (transformedProps) {
            transformedData = { ...transformedData, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    const PeriodChartId = uuidv4();

    let orderedColors =
        data?.themeSelected && paletteId
            ? getThemeBasedColorPalette(colorPalettes?.[paletteId], data?.colorSpace)
            : getColorPalette();

    // eslint-disable-next-line
    const customColorPallete: string[] = (data as any)?.colorPallete || [];

    if (customColorPallete.length > 0) {
        orderedColors = customColorPallete;
    }

    const seriesData = transformedData.performanceData || sampleData;

    let highChartOptions: Highcharts.Options = {
        chart: {
            type: 'spline',
            events: {
                load: function () {
                    if (!showBuffer) {
                        const xxchart = this;

                        // Adding the text to the chart renderer
                        this.renderer
                            .text(
                                i18n.format(rest.acceleratedZoneText || '', data) ||
                                    'Accelerated Zone',
                                60,
                                (xxchart.yAxis[0].toPixels(20.16, false) +
                                    xxchart.yAxis[0].toPixels(0, false)) /
                                    2 +
                                    10
                            )
                            .attr({
                                zIndex: 5,
                                opacity: 0.2,
                            })
                            .css({
                                fontSize: '32px',
                                color: '#3eb2ca',
                            })
                            .add();
                    }
                },
            },
        },

        title: {
            text: '',
        },

        exporting: {
            enabled: false,
        },

        credits: {
            enabled: false,
        },

        plotOptions: {
            spline: {
                marker: {
                    enabled: false,
                },
                lineWidth: 80,
            },
        },

        colors: orderedColors,

        xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: {
                day: '%b %e',
                week: '%b %e, %Y',
                month: '%b, %Y',
                year: '%Y',
            },
            labels: {
                y: 22,
            },
            lineWidth: 0,
            tickColor: '#e6e6e6',
        },

        yAxis: {
            tickInterval: 5,
            min: min ? Number(JsonQuery(min, { data: data })?.value) || Number(min) : null,
            max: max ? Number(JsonQuery(max, { data: data })?.value) || Number(max) : null,
            title: {
                text: '',
            },
            labels: {
                formatter(this: any) {
                    return this.value + '%';
                },
                style: {
                    color: '#1f3239',
                    fontSize: '11px',
                },
            },
            plotLines: plotLinesOptions,
            plotBands: plotBandOptions,
        },

        tooltip: {
            useHTML: true,
            formatter: function (this: any) {
                let text = '';

                // map based on tooltip options
                const toolTipOptions = showBuffer
                    ? toolTipBufferOptions
                    : toolTipAcceleratorOptions;

                if (toolTipOptions && !this.series?.options?.hideToolTip) {
                    text += `<div class="tooltip-options">`;
                    toolTipOptions.forEach((option) => {
                        text += `<div class="${option.className}">`;
                        text += `<span>${i18n.format(option.label || '', data)}</span>`;
                        const mappingValue = JsonQuery(option.mappingkey, {
                            data: this.point,
                        })?.value;
                        text += `<span>${
                            option.format ? i18n.t(mappingValue, option.format) : mappingValue
                        }</span>`;
                        text += '</div>';
                    });
                    text += '</div>';
                }

                return text;
            },
        },

        legend: {
            align: 'left',
            verticalAlign: 'top',
            layout: 'horizontal',
            x: 10,
            y: -10,
            itemStyle: {
                fontSize: '12px',
            },
        },

        series: transformedData.performanceData || sampleData,
    };

    highChartOptions = mergeOptions(
        transformedData.options,
        highChartOptions,
        { ...data, transformedData, seriesData },
        i18n,
        props.widgetInstanceId as string
    );

    return (
        <>
            <div
                className={`returns-bar-chart ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${PeriodChartStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
            >
                <HeaderWrapper headings={chartTitles} data={data} />
                <div id={`${PeriodChartId}`} className="js-period-chart-tile">
                    {['pdf', 'pptx'].includes(documentType) ? (
                        <div id={`${PeriodChartId}-period-chart`} />
                    ) : (
                        <HighchartsReact highcharts={Highcharts} options={highChartOptions} />
                    )}
                </div>
            </div>
            {['pdf', 'pptx'].includes(documentType) && (
                <>
                    <script
                        id={`${PeriodChartId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(highChartOptions),
                        }}
                    ></script>
                    <script
                        id={`${PeriodChartId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(performanceData) }}
                    ></script>
                </>
            )}
        </>
    );
};

export default PeriodChart;
